/**
 * External Imports
 */
import { FC, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { pdf } from '@react-pdf/renderer';
/**
 * Internal Imports
 */
import ComplianceHeader from '../../../components/searchHeader/complianceHeader/ComplianceHeader';
import ComplianceReportTable from '../../../components/customTables/reports/employee/complianceReportTable';
import { RootState } from '../../../redux/store';
import {
  clearComplianceReportFilter,
  getCSVToExportSuccess
} from '../../../redux/actions';
import EmployeeCompliancePdfTableFile from '../../../components/shared/buttons/DownloadPdfBtn/PdfTable/employee/EmployeeCompliancePdfTableFile';
import {
  calculateTotalCompliance,
  reduceComplianceByDept
} from '../../../utils/complianceFunctions';
import { date as dt } from '../../../utils/helpers';
/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  EmpComplianceReport as IComplianceReport,
  Filter,
  ReducedDeptComp,
  EmpDepartment
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { CReportContainer } from '../styles/ReportsViews.styles';
import { useGetEmployeeComplianceReportQuery } from '../../../redux/apis/Reports.api';

interface EmpComplianceReportProps {
  authorizedUser: AuthUserProps;
  empDepartments: EmpDepartment[];
  selectedEmpDepartments: EmpDepartment[];
  complianceReportFilter: Filter;
}

const EmpComplianceReport: FC<EmpComplianceReportProps> = ({
  authorizedUser,
  empDepartments = [],
  selectedEmpDepartments,
  complianceReportFilter
}) => {
  const dispatch = useDispatch();
  const [csvData, setCsvData] = useState<IComplianceReport[]>([]);
  const [listToDisplay, setListToDisplay] = useState<IComplianceReport[]>([]);
  const [listToDisplayTotal, setListToDisplayTotal] =
    useState<IComplianceReport | null>(null);

  useEffect(() => {
    return () => {
      dispatch(clearComplianceReportFilter());
    };
  }, []);

  const {
    data: complianceReportData,
    // isError: isGetEmployeeComplianceReportError,
    isFetching: isGetEmployeeComplianceReportFetching,
    isLoading: isGetEmployeeComplianceReportLoading
  } = useGetEmployeeComplianceReportQuery(
    {
      facilityId: authorizedUser?.facilityId,
      startDay: (
        complianceReportFilter.startDate ?? dt.firstDayOfMonth()
      ).replace(/-/g, ''),
      endDay: dt
        .addDayToDayOfYearDate(
          complianceReportFilter.endDate ?? dt.todayDateWithDashes()
        )
        .replace(/-/g, '')
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !authorizedUser?.facilityId
    }
  );

  useEffect(() => {
    if (empDepartments.length > 0) {
      const reducedCompliance = reduceComplianceByDept(
        complianceReportData ?? [],
        empDepartments
      );

      const complianceValues: ReducedDeptComp[] =
        Object.values(reducedCompliance);
      const complianceByDeptFilter = complianceValues.filter(
        (comp: ReducedDeptComp) =>
          selectedEmpDepartments.length > 0
            ? selectedEmpDepartments.some(
                unit => unit.departmentId === comp.groupId
              )
            : comp
      );

      const cleanCompliance = complianceByDeptFilter.map(
        (item: ReducedDeptComp) => {
          return {
            department: item.groupName,
            locationTypeDisplay: item.locTypeDisplay,
            departmentId: item.groupId,
            total: item.combinedData.total,
            roundedPercent: item.combinedData.roundedPct,
            roundedNumber: item.combinedData.rounded,
            notRoundedPercent: item.combinedData.nonePct,
            notRoundedNumber: item.combinedData.none
          };
        }
      );
      setListToDisplay(cleanCompliance);
      if (selectedEmpDepartments.length > 0) {
        setListToDisplayTotal(
          calculateTotalCompliance(
            cleanCompliance,
            selectedEmpDepartments.length === empDepartments.length
              ? 'Facility Total'
              : 'Selected Departments Total',
            selectedEmpDepartments.length === empDepartments.length
              ? 'FACILITY'
              : selectedEmpDepartments.map(d => d.departmentId).join(','),
            'employee'
          )
        );
      } else {
        setListToDisplayTotal(null);
      }
      setCsvData(cleanCompliance);
    }
  }, [complianceReportData, selectedEmpDepartments, empDepartments]);

  const handleExportCsv = () => {
    dispatch(getCSVToExportSuccess(csvData));
  };

  const handleClickDownloadPDF = async () => {
    const asPdf = pdf(<EmployeeCompliancePdfTableFile data={csvData} />);

    const blob = await asPdf.toBlob();
    function downloadBlob(name = 'file.txt') {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = name;

      document.body.appendChild(link);
      link.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        })
      );
      document.body.removeChild(link);
    }

    return downloadBlob(
      `emp-compliance-report-${new Date().toISOString()}.pdf`
    );
  };

  return (
    <CReportContainer id="Compliance-Report-Container">
      <ComplianceHeader
        handleExportCsv={handleExportCsv}
        handleClickDownloadPDF={handleClickDownloadPDF}
      />
      <ComplianceReportTable
        complianceList={listToDisplay}
        complianceTotal={listToDisplayTotal}
        loading={
          isGetEmployeeComplianceReportLoading ||
          isGetEmployeeComplianceReportFetching
        }
      />
    </CReportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    complianceReportFilter: state.ReportReducer.complianceReportFilter,
    empDepartments: state.ConfigReducer.empDepartments,
    selectedEmpDepartments: state.ConfigReducer.selectedEmpDepartments
  };
};

export default connect(mapReduxStateToProps)(EmpComplianceReport);
